import React, { useEffect, useRef } from 'react';

import './HypstaLanding.css';

const products = [
  { id: 'hypes', name: 'Hypes.in', title: 'Connect. Collaborate. Grow.', description: 'A business collaboration platform designed to help businesses, professionals, investors, and service providers discover opportunities and connect within one ecosystem.' },
  { id: 'toss', name: 'TOSS Index', title: 'One Score. Product Sustainability Picture.', description: 'A product sustainability scoring index that converts lifecycle, environmental, social, governance, and economic factors into a simple, comparable sustainability score.' },
  { id: 'sustaindex', name: 'SustainDex', title: 'Sustainability Data, Made Accessible.', description: 'A sustainability data exchange platform that organizes, compares, and shares environmental and sustainability datasets for LCA, carbon accounting, ESG reporting, research, and informed decision-making.' },
];

export default function HypstaLanding() {
  const surface = useRef(null);
  const video = useRef(null);
  const rippleLayer = useRef(null);
  const lastRipple = useRef(0);

  const requestDialog = useRef(null);
  useEffect(() => {
    document.title = 'Hypsta — Innovate. Sustain. Progress.';
    const description = document.querySelector('meta[name="description"]');
    if (description) description.content = 'Discover Hypes.in, TOSS, and SustainDex. Three products from Hypsta Business Solution Pvt Ltd connecting business, sustainability, and data.';
    const favicon = document.querySelector('link[rel="icon"]');
    if (favicon) favicon.href = '/hypsta/favicon.svg';
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      entry.target.classList.toggle('is-visible', entry.isIntersecting);
    }), { threshold: 0.12 });
    surface.current.querySelectorAll('.hypsta-reveal').forEach(element => observer.observe(element));
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (media.matches) { video.current.pause(); }
    return () => observer.disconnect();
  }, []);

  function move(event) {
    if (event.pointerType === 'touch' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const rect = event.currentTarget.getBoundingClientRect();
    event.currentTarget.style.setProperty('--pointer-x', `${(event.clientX - rect.left) / rect.width * 100}%`);
    event.currentTarget.style.setProperty('--pointer-y', `${(event.clientY - rect.top) / rect.height * 100}%`);
  }

  function ripple(event) {
    if (event.pointerType !== 'mouse' || window.matchMedia('(prefers-reduced-motion: reduce)').matches || performance.now() - lastRipple.current < 110) return;
    lastRipple.current = performance.now();
    const ring = document.createElement('i');
    ring.style.left = `${event.clientX}px`;
    ring.style.top = `${event.clientY}px`;
    ring.addEventListener('animationend', () => ring.remove(), { once: true });
    rippleLayer.current.appendChild(ring);
  }

  function explore(event) {
    event.preventDefault();
    document.getElementById('products').scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }

  function requestIndex(event) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const body = ['Hello, I would like to request a TOSS Index assessment.', '',
      ...['Business name', 'Contact name', 'Business email', 'Product / category', 'Business location', 'Requirements'].map(label => `${label}: ${data.get(label) || 'Not provided'}`)].join('\n');
    window.location.href = `mailto:hypes.in.global@gmail.com?subject=${encodeURIComponent('TOSS Index request — ' + data.get('Business name'))}&body=${encodeURIComponent(body)}`;
  }

  return <div className="hypsta-site" ref={surface} onPointerMove={ripple}><div className="hypsta-ripple-layer" ref={rippleLayer} aria-hidden="true" />
    <header className="hypsta-header">
      <div className="hypsta-brand"><img className="hypsta-clean-nav-logo" src="/hypsta/hbs-mark-new.png" alt="HBS — Hypsta Business Solution Pvt Ltd" /><span className="hypsta-header-note">Hypsta Business Solution Pvt Ltd</span></div>
      <a className="hypsta-nav" href="#products" onClick={explore}>Our ecosystem <span aria-hidden="true">↗</span></a>
    </header>

    <dialog ref={requestDialog} className="hypsta-request-dialog">
      <button className="hypsta-dialog-close" aria-label="Close request form" onClick={() => requestDialog.current.close()}>×</button>
      <h2>Request your TOSS Index</h2>
      <p>Tell us a little about your business and product.</p>
      <form onSubmit={requestIndex}>
        {['Business name', 'Contact name', 'Business email', 'Product / category', 'Business location'].map(label => <label key={label}>{label}<input name={label} type={label === 'Business email' ? 'email' : 'text'} required maxLength={160} /></label>)}
        <label>Requirements <span>(optional)</span><textarea name="Requirements" rows="3" maxLength={1200} /></label>
        <button type="submit" className="hypsta-email-button">Continue to email ↗</button>
        <p className="hypsta-mail-note">Opens your email app with these details. Review and send to <a href="mailto:hypes.in.global@gmail.com">hypes.in.global@gmail.com</a>.</p>
      </form>
    </dialog>
    <main>
      <section className="hypsta-hero" onPointerMove={move}>
        <div className="hypsta-aurora" aria-hidden="true"><i /><i /><i /></div>
        <div className="hypsta-hero-content">
          <p className="hypsta-eyebrow"><span /> INNOVATE. SUSTAIN. PROGRESS.</p>
          <h1>Building<br /><span>for what’s next.</span></h1>
          <p className="hypsta-intro">Enabling businesses to evolve with purpose, through solutions designed for growth, resilience, and a more sustainable tomorrow.</p>
          <a className="hypsta-explore" href="#products" onClick={explore}>Discover our ecosystem <span aria-hidden="true">↓</span></a>
        </div>
        <div className="hypsta-hero-video"><video ref={video} autoPlay muted loop playsInline preload="metadata" poster="/hypsta/hbs-transparent.png" aria-label="Animated HBS logo" onError={event => { event.currentTarget.style.display = 'none'; event.currentTarget.nextElementSibling.hidden = false; }}><source src="/hypsta/hbs-hero-transparent.webm" type="video/webm" /></video><img hidden className="hypsta-video-fallback" src="/hypsta/hbs-transparent.png" alt="HBS — Hypsta Business Solution Pvt Ltd" /></div>
      </section>

      <section id="products" className="hypsta-sanctuary">
        <div className="hypsta-sanctuary-heading hypsta-reveal"><h2 className="hypsta-eyebrow">THE HYPSTA FAMILY</h2><img src="/hypsta/hbs-transparent.png" alt="Hypsta Business Solution Pvt Ltd — Innovate. Sustain. Progress." /></div>
        <div className="hypsta-sanctuary-display hypsta-reveal">
          <img className="hypsta-stone-platform" src="/hypsta/brand-stone-stage-v3.png" alt="Purple, green, and gold crystal stones on a natural stone shelf" />
          <div className="hypsta-stone-brands" aria-hidden="true">
            <img className="hypsta-on-stone-hypes" src="/Hypes Logo.png" alt="" />
            <img className="hypsta-on-stone-toss" src="/hypsta/toss-white.png" alt="" />
            <img className="hypsta-on-stone-sustain" src="/hypsta/sustain-purple.png" alt="" />
          </div>
        </div>
        <div className="hypsta-sanctuary-cards">
          {products.map(product => <article key={product.id} className={`hypsta-sanctuary-card hypsta-sanctuary-${product.id} hypsta-reveal`}>
            <h3>{product.name}</h3>

            <p className="hypsta-card-tagline">{product.title}</p>
            <p className="hypsta-sanctuary-description">{product.description}</p>
            {product.id === 'toss' && <button className="hypsta-card-link" onClick={() => requestDialog.current.showModal()}>Request an index ↗</button>}
            {product.id === 'sustaindex' && <a className="hypsta-card-link" href="https://sustaindex.in" target="_blank" rel="noopener noreferrer">Visit SustainDex ↗</a>}
            {product.id === 'hypes' && <button className="hypsta-card-link" disabled>Coming soon</button>}
          </article>)}
        </div>
      </section>
      <section className="hypsta-closing hypsta-reveal"><h2>Connecting ideas, opportunities, and innovation <span>to build a <em className="closing-green">better</em> <em className="closing-gold">business</em> <em className="closing-purple">ecosystem.</em></span></h2></section>
      <div className="hypsta-bottom-stones"><img src="/hypsta/stones-transparent.png" alt="Three polished stones in yellow, blue, and green" loading="lazy" /></div>
    </main>
    <footer className="hypsta-footer"><div><strong>hypsta.</strong><p>Hypsta Business Solution Pvt Ltd</p></div><p>Innovate · Sustain · Progress</p><span>© {new Date().getFullYear()} Hypsta</span></footer>
  </div>;
}
